import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FALLBACK_PRODUCTS,
    PRODUCT_PAGES,
    productForReturnSlug,
} from '../utils/productCatalogue';
import { hasProductDraft } from '../utils/productDraft';
import { readPurchaseInFlight } from '../utils/purchaseMarker';
import { ProductKey } from '../types/billing';
import '../styles/PaymentStyles.scss';

/**
 * The page to go back to: the product's own, or the page its run is set up on when that is
 * elsewhere and the input left there for the checkout is waiting to be put back.
 */
const backRouteFor = (product: ProductKey): string => {
    const page = PRODUCT_PAGES[product];
    return page.formRoute && hasProductDraft(product) ? page.formRoute : page.route;
};

const count = (n: number) => n.toLocaleString('en-US');

/**
 * Where Stripe sends the buyer after paying. Outside sign-in, so it has neither the header nor
 * the wallet: the Credits are counted once the buyer is back in the app.
 *
 * A checkout started from a product carries `?return=<slug>`. Only the slugs in the product
 * table are followed — the value arrives through a URL anyone can edit — and with one the page
 * waits for the buyer to choose where to go rather than moving on by itself, because the
 * product page is where their unfinished run is.
 *
 * What was bought, and how many runs of that product it pays for, comes from the note the app
 * left in this tab before the redirect. Without one (another tab, storage switched off) the page
 * says only that the Credits are on their way.
 */
const PaymentSuccess: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const returnProduct = useMemo(
        () => productForReturnSlug(new URLSearchParams(location.search).get('return')),
        [location.search],
    );
    // Read, not taken: the wallet takes it once the buyer is back in the app, to wait for the
    // Credits to land.
    const note = useMemo(() => readPurchaseInFlight(), []);
    // The note speaks for this page's product only when it was left for the same one.
    const noteForReturn = returnProduct !== null && note?.returnProduct === returnProduct ? note : null;
    // The name as the catalogue gave it when the checkout started, carried over in the note the
    // app left in this tab. The page cannot ask the catalogue itself — it sits outside sign-in —
    // so a note from another tab, or none, falls back to the table kept for when the catalogue
    // cannot be reached.
    const returnName = returnProduct
        ? (noteForReturn?.returnName ??
            FALLBACK_PRODUCTS.find((product) => product.product === returnProduct)?.name ??
            null)
        : null;

    const credits = note?.credits ?? null;
    const perRun = noteForReturn?.perRunCredits ?? null;
    const runs = credits !== null && perRun !== null ? Math.floor(credits / perRun) : null;

    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        if (returnProduct) return;
        const countdownInterval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    navigate('/');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, [navigate, returnProduct]);

    return (
        <div className="page-payment">
            <div className="payment-container success">
                <i className="fas fa-check-circle icon-success"></i>
                <h2>Payment Successful!</h2>
                {credits !== null ? (
                    <p>
                        Thank you for your purchase. {count(credits)} {credits === 1 ? 'credit' : 'credits'} will be
                        added to your wallet shortly.
                        {/* A purchase smaller than one run adds to the balance, but says nothing
                            here about runs it cannot pay for on its own. */}
                        {runs !== null && runs >= 1 && returnName && (
                            <> That&apos;s enough for {count(runs)} {returnName} {runs === 1 ? 'run' : 'runs'}.</>
                        )}
                    </p>
                ) : (
                    <p>Thank you for your purchase. Your credits will be added to your account shortly.</p>
                )}
                {returnProduct ? (
                    <div className="payment-actions">
                        <button
                            className="btn-primary"
                            onClick={() => navigate(backRouteFor(returnProduct))}
                        >
                            Back to {returnName}
                        </button>
                        <button className="btn-secondary" onClick={() => navigate('/')}>
                            Back to products
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="redirect-countdown">
                            <p className="countdown-text">Redirecting in <span className="countdown-number">{countdown}</span> second{countdown !== 1 ? 's' : ''}...</p>
                        </div>
                        <button className="btn-primary" onClick={() => navigate('/')}>
                            Return to Dashboard
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
